<template>
  <div
    class="mx-auto max-w-content-container"
    data-testid="sustainability-index-section"
  >
    <div class="flex flex-col gap-10 xl:flex-row">
      <div class="flex flex-col gap-6">
        <SustainabilityIndexIntro
          :market="market"
          class="text-sm lg:max-w-3xl"
        />
        <ButtonContactUs
          v-if="!loggedIn"
          :services="initialFields"
          text="Contact Us"
          class="-mt-2 w-full px-[31.07px] py-[21px] text-sm"
        />
        <CommonButtonDownload
          v-if="isAuthenticated && report"
          class="min-w-[140px] max-w-full !rounded lg:max-w-fit"
          :download-file="reportFile"
          variant="secondary"
          data-testid="lithium-sustainability-download-button"
        />
      </div>
      <div class="w-[100%] lg:min-h-[300px] lg:min-w-[500px] lg:max-w-[500px]">
        <PlayerVideoVimeoModal data-testid="video-modal" :vimeo-id="vimeoId" />
      </div>
    </div>
    <SustainabilityIndexTable
      :key="activeMineral?.slug"
      :sustainability-indexes="sustainabilityIndexes"
      :product="product"
    />
  </div>
</template>

<script setup lang="ts">
import CommonButtonDownload from '~/components/shared/common/ButtonDownload.vue'
import PlayerVideoVimeoModal from '~/components/video/PlayerVideoVimeoModal.vue'
import ButtonContactUs from '~/components/shared/common/ButtonContactUs.vue'
import SustainabilityIndexTable from '~/components/sustainability/SustainabilityIndexTable.vue'
import SustainabilityIndexIntro from '~/components/sustainability/SustainabilityIndexIntro.vue'

import type { TMarketServiceConfig } from '~/src/types/markets'
import { logError } from '~/utils/log-error'
import useAuthentication from '~/composables/useAuthentication'
import { useAuthorization } from '~/composables/useAuthorization'

/*-------------------------------------
  Props & Refs
-------------------------------------*/
const props = defineProps<{
  market: TMarketServiceConfig
  title: string
  sustainabilityIndexes: Array<any>
  product: string
  subscription: string
  vimeoId: number | string
  initialFields: string[]
}>()

const reports = ref(null)
const report = ref(null)
const isAuthenticated = ref(false)
const activeMineral = ref(props.sustainabilityIndexes[0])

const categoryReport = computed(() => {
  return props.subscription === 'esg-cobalt' ||
    props.subscription === 'esg-nickel'
    ? 'esg-nickel-and-cobalt'
    : props.subscription
})

const reportFile = computed(() => {
  return {
    ...report.value,
    category: categoryReport.value,
  }
})

const { $downloadManager } = useNuxtApp()
const { hasAuthorized } = useAuthorization()
const { loggedIn } = useAuthentication()

/*-------------------------------------
  Methods
-------------------------------------*/
const getReportData = async () => {
  try {
    if (loggedIn.value) {
      isAuthenticated.value = await hasAuthorized(props.subscription)
    }
    if (isAuthenticated.value) {
      const tag = activeMineral.value.tag || 'esg-matrix'
      if (!reports.value) {
        reports.value = await $downloadManager.getAllReports({
          market: props.market.slug,
          marketTypes: ['esg'],
          categories: [categoryReport.value],
          tags: [tag],
        })
      }
      report.value = reports.value?.filter((item: any) =>
        item.tags.some((_tag: object) => _tag.slug === tag),
      )[0]
    }
  } catch (error) {
    logError(error)
  }
}

/*-------------------------------------
  Lifecycle
-------------------------------------*/
onMounted(getReportData)
</script>
